import { Search } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { Label } from "@src/shared/components/ui/label";
import {
  Combobox,
  ComboboxAnchor,
  ComboboxContent,
  ComboboxInput,
  type ComboboxInputValueChangeMeta,
} from "@src/shared/components/v2/Combobox";
import { useControllableOpen } from "@src/shared/hooks/useControllableOpen";
import { cn } from "@src/shared/lib/utils";

import { PatientSearchResults } from "./components";
import { usePatientOpener, usePatientSearchOptions } from "./hooks";
import type {
  PatientSearchOption,
  PatientSearchV2Props,
} from "./PatientSearch.types";

export type {
  PatientSearchProps,
  PatientSearchV2Props,
} from "./PatientSearch.types";

export function PatientSearchV2({
  open,
  defaultOpen = false,
  disabled = false,
  autoFocus = false,
  label = "Search patients",
  placeholder = "Search a patient",
  limit = 6,
  navigateStateSource = "v2 patient search",
  className,
  inputClassName,
  contentClassName,
  listboxLabel = "Patient search results",
  emptyMessage = "No patients found.",
  trailingAction,
  onOpenChange,
}: PatientSearchV2Props) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const patientOpener = usePatientOpener(navigateStateSource);
  const isDisabled = disabled || patientOpener.isOpening;
  const { resolvedOpen, setOpen: setSearchOpen } = useControllableOpen({
    value: open,
    defaultValue: defaultOpen,
    disabled: isDisabled,
    onChange: onOpenChange,
  });
  const patientSearch = usePatientSearchOptions({
    disabled,
    limit,
    query,
  });

  useEffect(() => {
    if (!autoFocus) return;
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [autoFocus]);

  const handleInputValueChange = (
    value: string,
    meta: ComboboxInputValueChangeMeta,
  ) => {
    if (meta.source === "input" || meta.source === "clear") {
      setQuery(value);
      if (!resolvedOpen) setSearchOpen(true);
    }
  };

  return (
    <Combobox<PatientSearchOption>
      closeOnSelect={false}
      itemToStringValue={(option) => `${option.name} ${option.metadata ?? ""}`}
      itemToValue={(option) => option.key}
      items={patientSearch.options}
      label={label}
      open={resolvedOpen}
      resetInputOnClose={false}
      shouldFilter={false}
      onOpenChange={setSearchOpen}
    >
      <ComboboxAnchor asChild>
        <div
          className={cn("relative w-full", className)}
          data-combobox-anchor=""
        >
          <Label htmlFor={inputId} className="sr-only">
            {label}
          </Label>
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <ComboboxInput
            ref={inputRef}
            asAnchor={false}
            id={inputId}
            placeholder={placeholder}
            autoComplete="off"
            className={cn(
              "h-10 rounded-[11px] border-border bg-background pr-10 pl-9 text-[13px] shadow-[0_1px_2px_rgba(20,23,26,0.04)] focus-visible:ring-0",
              trailingAction && "pr-12",
              inputClassName,
            )}
            disabled={isDisabled}
            onFocus={() => setSearchOpen(true)}
            onValueChange={handleInputValueChange}
            showClear
          />
          {trailingAction ? (
            <div className="absolute top-1/2 right-1.5 -translate-y-1/2">
              {trailingAction}
            </div>
          ) : null}
        </div>
      </ComboboxAnchor>

      <ComboboxContent
        align="start"
        portalled={false}
        sideOffset={6}
        className={cn(
          "overflow-hidden rounded-[10px] p-0 shadow-[0_18px_45px_rgba(15,23,42,0.14)]",
          contentClassName,
        )}
      >
        <PatientSearchResults
          emptyMessage={emptyMessage}
          error={patientSearch.error}
          isError={patientSearch.isError}
          isOpening={patientOpener.isOpening}
          isSearching={patientSearch.isSearching}
          listboxLabel={listboxLabel}
          onSelect={patientOpener.openPatient}
          openingKey={patientOpener.openingKey}
          options={patientSearch.options}
        />
      </ComboboxContent>
    </Combobox>
  );
}

export const PatientSearch = PatientSearchV2;
